port module Frontend exposing (Flags, Model, Msg, main)

import Backend.Code exposing (CodeId)
import Backend.ColorScheme exposing (ColorScheme)
import Backend.File as File exposing (File)
import Backend.Settings.ClosedAfter as ClosedAfter
import Browser
import Codec exposing (Codec)
import Codec.Extra as Codec
import Color.Convert as Color
import Frontend.Api
import Frontend.Checkout as Checkout exposing (Checkout)
import Frontend.Discount as Discount exposing (Discount)
import Frontend.FileUpload as FileUpload exposing (FileUploads)
import Frontend.FileUpload.Internal as FileUpload exposing (FileUploads)
import Frontend.Form as Form
import Frontend.Markdown as Markdown
import Frontend.Order as Order exposing (Order)
import Frontend.OrderInput as OrderInput
import Frontend.Page as Page exposing (Page)
import Frontend.Settings exposing (Settings)
import Frontend.Ticket as Ticket exposing (Ticket)
import Html exposing (Html)
import Html.Attributes exposing (class)
import Http
import Http.Extra as Http
import Json.Decode as Decode exposing (Decoder)
import Json.Encode exposing (Value)
import List.NonEmpty as NonEmpty exposing (NonEmpty)
import Locale exposing (Locale)
import Material.CircularProgress as CircularProgress
import Maybe.Extra as Maybe
import Result.Extra as Result
import Task
import Time
import Translated exposing (Translated)
import Url exposing (Url)


type alias Flags =
    { values : Maybe Value
    , pages : Maybe Value
    , idempotencyToken : String
    , locale : Maybe String
    , tickets : Maybe Value
    , discounts : Maybe Value
    , code : Maybe String
    , id : Maybe String
    , crashed : Maybe String
    , checkout : Maybe Value
    , loading : Maybe Bool
    , origin : String
    }


main : Program Flags Model Msg
main =
    Browser.element
        { init = init
        , subscriptions = subscriptions
        , update = update
        , view = view
        }


type Error
    = CustomError String
    | DecodeError Decode.Error
    | HttpError Http.Error
    | MarkdownError String


errorToString : Error -> String
errorToString error =
    case error of
        DecodeError decodeError ->
            "decode error: " ++ Decode.errorToString decodeError

        HttpError httpError ->
            "http error: " ++ Http.errorToString httpError

        MarkdownError markdownError ->
            "markdown error: " ++ markdownError

        CustomError customError ->
            customError


type Model
    = Crashed CrashedData
    | QuotaExceeded QuotaExceededData
    | Loaded LoadedData
    | Loading
    | Finished FinishedData
    | Paying PayingData
    | Closed ClosedData


type alias PayingData =
    { defaultLocale : Locale
    , locale : Locale
    , checkout : Checkout
    }


type alias CrashedData =
    { defaultLocale : Locale
    , locale : Locale
    , error : Error
    }


type alias QuotaExceededData =
    { defaultLocale : Locale
    , locale : Locale
    , code : String
    }


type alias LoadedData =
    { pages : List Page
    , form : Form.Values
    , idempotencyToken : String
    , defaultLocale : Locale
    , locale : Locale
    , code :
        Maybe
            { code : String
            , codeId : CodeId
            }
    , tickets : List Ticket
    , discounts : List Discount
    , settings : Settings
    , confirmationText : Translated String
    , fileUploads : FileUploads
    , origin : Maybe Url
    }


type alias FinishedData =
    { confirmationText : Translated String
    , defaultLocale : Locale
    , locale : Locale
    , idempotencyToken : String
    }


type alias ClosedData =
    { closedText : Translated String
    , defaultLocale : Locale
    , locale : Locale
    }


type Msg
    = PagesChanged (Result Decode.Error (List Page))
    | FormChanged (Form.Msg Form.Values Order)
    | LocaleChanged (Result Decode.Error Locale)
    | Submitted (Result Http.Error Submission)
    | TicketsChanged (Result Decode.Error (List Ticket))
    | DiscountsChanged (Result Decode.Error (List Discount))
    | Initialized ( Model, Cmd Msg )
    | Uploaded String File (Result Http.Error String)


type Submission
    = PaymentRequired PaymentRequiredData
    | SubmissionException SubmissionExceptionData
    | SubmissionOk SubmissionOkData


submissionDecoder : Decoder Submission
submissionDecoder =
    Decode.oneOf
        [ Decode.map PaymentRequired (Codec.decoder paymentRequiredDataCodec)
        , Decode.map SubmissionException submissionExceptionDecoder
        , Decode.map SubmissionOk (Codec.decoder submissionOkDataCodec)
        ]



{-
   submissionCodec : Codec Submission
   submissionCodec =
       Codec.custom
           (\paymentRequired submissionException submissionOk value ->
               case value of
                   PaymentRequired data ->
                       paymentRequired data

                   SubmissionException data ->
                       submissionException data

                   SubmissionOk data ->
                       submissionOk data
           )
           |> Codec.variant1 "PaymentRequired"
               PaymentRequired
               paymentRequiredDataCodec
           |> Codec.variant1 "SubmissionException"
               SubmissionException
               submissionExceptionDataCodec
           |> Codec.variant1 "SubmissionOk"
               SubmissionOk
               submissionOkDataCodec
           |> Codec.buildCustom
-}


type alias PaymentRequiredData =
    { brands : NonEmpty String
    , checkoutId : String
    , endpoint : Url
    , shopperResultUrl : Url
    , locale : Locale
    }


paymentRequiredDataCodec : Codec PaymentRequiredData
paymentRequiredDataCodec =
    Codec.object PaymentRequiredData
        |> Codec.field "brands" .brands (Codec.nonEmpty Codec.string)
        |> Codec.field "checkoutId" .checkoutId Codec.string
        |> Codec.field "endpoint" .endpoint Codec.url
        |> Codec.field "shopperResultUrl" .shopperResultUrl Codec.url
        |> Codec.field "locale" .locale Locale.codec
        |> Codec.buildObject


type alias SubmissionExceptionData =
    { error : String
    }


submissionExceptionDecoder : Decoder SubmissionExceptionData
submissionExceptionDecoder =
    Decode.map SubmissionExceptionData
        (Decode.oneOf
            [ Decode.field "error" Decode.string
            , Decode.field "error" (Decode.field "message" Decode.string)
            ]
        )


type alias SubmissionOkData =
    { id : String
    }


submissionOkDataCodec : Codec SubmissionOkData
submissionOkDataCodec =
    Codec.object SubmissionOkData
        |> Codec.field "transactionId" .id Codec.string
        |> Codec.buildObject


port submit : Value -> Cmd msg


port inspect : Value -> Cmd msg


port navigated : () -> Cmd msg


port mountCheckout : Value -> Cmd msg


init : Flags -> ( Model, Cmd Msg )
init flags =
    let
        fetchCode =
            Maybe.unwrap (Task.succeed (Ok Nothing))
                (\code ->
                    Task.map
                        (Result.map
                            (Just
                                << (\codeId ->
                                        { code = code
                                        , codeId = codeId
                                        }
                                   )
                            )
                        )
                        (Frontend.Api.redeemCode
                            { code = code
                            }
                        )
                )
                flags.code
    in
    ( Loading
    , Task.map3
        (\now settings codeResult ->
            { now = now
            , settings = settings
            , codeResult = codeResult
            }
        )
        Time.now
        Frontend.Api.fetchSettings
        fetchCode
        |> Task.map
            (\{ now, settings, codeResult } ->
                let
                    defaultLocale =
                        NonEmpty.head settings.locales

                    locale =
                        Maybe.unwrap defaultLocale Locale.fromString flags.locale
                in
                [ case flags.loading of
                    Just True ->
                        Just ( Loading, Cmd.none )

                    _ ->
                        Nothing
                , Maybe.map
                    (\customError ->
                        ( Crashed
                            { defaultLocale = defaultLocale
                            , locale = locale
                            , error = CustomError customError
                            }
                        , Cmd.none
                        )
                    )
                    flags.crashed
                , Maybe.map
                    (\checkoutValue ->
                        case Decode.decodeValue (Codec.decoder Checkout.codec) checkoutValue of
                            Ok checkout ->
                                ( Paying
                                    { defaultLocale = defaultLocale
                                    , locale = locale
                                    , checkout = checkout
                                    }
                                , mountCheckout (Codec.encoder Checkout.codec checkout)
                                )

                            Err decodeError ->
                                ( Crashed
                                    { defaultLocale = defaultLocale
                                    , locale = locale
                                    , error = DecodeError decodeError
                                    }
                                , Cmd.none
                                )
                    )
                    flags.checkout
                , Maybe.map
                    (\id ->
                        ( Loading
                        , Task.perform Initialized
                            (Task.onError
                                (\error ->
                                    Task.succeed
                                        ( Crashed
                                            { defaultLocale = defaultLocale
                                            , locale = locale
                                            , error = error
                                            }
                                        , Cmd.none
                                        )
                                )
                                (Task.mapError HttpError
                                    (Task.map
                                        (\_ ->
                                            ( Finished
                                                { confirmationText = settings.confirmationText
                                                , defaultLocale = defaultLocale
                                                , locale = locale
                                                , idempotencyToken = id
                                                }
                                            , Cmd.none
                                            )
                                        )
                                        (Frontend.Api.fetchStatus { id = id })
                                    )
                                )
                            )
                        )
                    )
                    flags.id
                , Maybe.andThen
                    (\closedAfter ->
                        if
                            ClosedAfter.isClosed
                                (case codeResult of
                                    Err _ ->
                                        Just ()

                                    Ok Nothing ->
                                        Nothing

                                    Ok (Just _) ->
                                        Just ()
                                )
                                now
                                closedAfter
                        then
                            Just
                                ( Closed
                                    { defaultLocale = defaultLocale
                                    , locale = locale
                                    , closedText = settings.closedText
                                    }
                                , Cmd.none
                                )

                        else
                            Nothing
                    )
                    settings.closedAfter
                ]
                    |> Maybe.values
                    |> List.head
                    |> Maybe.withDefault
                        (let
                            fetchPages =
                                Maybe.unwrap
                                    (Task.mapError HttpError Frontend.Api.fetchPages)
                                    (\pages ->
                                        Task.mapError DecodeError
                                            (Result.toTask
                                                (Decode.decodeValue (Decode.list Page.decoder)
                                                    pages
                                                )
                                            )
                                    )
                                    flags.pages

                            fetchTickets =
                                Maybe.unwrap
                                    (Task.mapError HttpError
                                        (Frontend.Api.fetchTickets
                                            { code = flags.code
                                            }
                                        )
                                    )
                                    (\tickets ->
                                        Task.mapError DecodeError
                                            (Result.toTask
                                                (Decode.decodeValue (Decode.list Ticket.decoder)
                                                    tickets
                                                )
                                            )
                                    )
                                    flags.tickets

                            fetchDiscounts =
                                Maybe.unwrap (Task.mapError HttpError Frontend.Api.fetchDiscounts)
                                    (\discounts ->
                                        Task.mapError DecodeError
                                            (Result.toTask
                                                (Decode.decodeValue (Decode.list Discount.decoder)
                                                    discounts
                                                )
                                            )
                                    )
                                    flags.discounts

                            fetchForm =
                                Maybe.unwrap
                                    (Task.succeed Form.initialValues)
                                    (\values ->
                                        Task.mapError DecodeError
                                            (Result.toTask
                                                (Decode.decodeValue
                                                    (Decode.map (Maybe.withDefault Form.initialValues)
                                                        (Decode.nullable Form.valuesDecoder)
                                                    )
                                                    values
                                                )
                                            )
                                    )
                                    flags.values
                         in
                         ( Loading
                         , Task.perform Initialized <|
                            Task.onError
                                (\error ->
                                    Task.succeed
                                        ( Crashed
                                            { defaultLocale = defaultLocale
                                            , locale = locale
                                            , error = error
                                            }
                                        , Cmd.none
                                        )
                                )
                                (Task.map4
                                    (\pages tickets discounts form ->
                                        case codeResult of
                                            Err (Frontend.Api.QuotaExceeded code) ->
                                                ( QuotaExceeded
                                                    { defaultLocale = defaultLocale
                                                    , locale = locale
                                                    , code = code
                                                    }
                                                , Cmd.none
                                                )

                                            Ok code ->
                                                let
                                                    loadedData =
                                                        { pages = pages
                                                        , form = form
                                                        , idempotencyToken = flags.idempotencyToken
                                                        , defaultLocale = defaultLocale
                                                        , locale = locale
                                                        , code = code
                                                        , tickets = tickets
                                                        , discounts = discounts
                                                        , settings = settings
                                                        , confirmationText = settings.confirmationText
                                                        , fileUploads = FileUpload.empty
                                                        , origin = Url.fromString flags.origin
                                                        }
                                                in
                                                ( Loaded loadedData
                                                , inspect (Form.encodeValues loadedData.form)
                                                )
                                    )
                                    fetchPages
                                    fetchTickets
                                    fetchDiscounts
                                    fetchForm
                                )
                         )
                        )
            )
        |> Task.onError
            (\error ->
                Task.succeed
                    ( Crashed
                        { defaultLocale = Locale.Known Locale.De
                        , locale = Locale.Known Locale.De
                        , error = HttpError error
                        }
                    , Cmd.none
                    )
            )
        |> Task.perform Initialized
    )


port setPages : (Value -> msg) -> Sub msg


port setLocale : (String -> msg) -> Sub msg


port setTickets : (Value -> msg) -> Sub msg


port setDiscounts : (Value -> msg) -> Sub msg


subscriptions : Model -> Sub Msg
subscriptions _ =
    Sub.batch
        [ setPages (PagesChanged << Decode.decodeValue (Decode.list Page.decoder))
        , setLocale (LocaleChanged << Ok << Locale.fromString)
        , setTickets (TicketsChanged << Decode.decodeValue (Decode.list Ticket.decoder))
        , setDiscounts (DiscountsChanged << Decode.decodeValue (Decode.list Discount.decoder))
        ]


update : Msg -> Model -> ( Model, Cmd Msg )
update msg model =
    let
        pass =
            ( model, Cmd.none )
    in
    case ( msg, model ) of
        ( Initialized cont, Loading ) ->
            cont

        ( Initialized _, _ ) ->
            pass

        ( _, Loading ) ->
            pass

        ( _, Crashed _ ) ->
            pass

        ( _, QuotaExceeded _ ) ->
            pass

        ( FormChanged (Form.Submitted order), Loaded loadedData ) ->
            let
                orderInput =
                    OrderInput.fromOrder (Page.globalTicketFields loadedData.pages)
                        order

                discountPrice =
                    Maybe.unwrap 0 (.price << Tuple.second) <|
                        Discount.applys loadedData
                            orderInput
                            loadedData.discounts

                frontendPrice =
                    OrderInput.totalPrice loadedData orderInput
                        - discountPrice
            in
            ( Loaded loadedData
            , Cmd.batch
                [ submit (Order.encode order)
                , Http.post
                    { body = Http.jsonBody (Order.encode order)
                    , expect = Http.expectJson Submitted submissionDecoder
                    , url = "http://localhost:8080/public/payment/prepare?id=" ++ loadedData.idempotencyToken ++ "&frontendPrice=" ++ String.fromInt frontendPrice ++ Maybe.unwrap "" ((++) "&referringUrl=" << Url.percentEncode << Url.toString) loadedData.origin
                    }
                ]
            )

        ( FormChanged (Form.Changed newForm), Loaded loadedData ) ->
            let
                pageChanged =
                    newForm.cursor /= loadedData.form.cursor
            in
            ( Loaded
                { loadedData
                    | form = newForm
                }
            , Cmd.batch
                [ inspect (Form.encodeValues newForm)
                , if pageChanged then
                    navigated ()

                  else
                    Cmd.none
                ]
            )

        ( FormChanged (Form.UploadRequested maxSize key file), Loaded loadedData ) ->
            let
                ( newFileUploads, newForm ) =
                    FileUpload.insertPending maxSize
                        key
                        file
                        loadedData.fileUploads
                        loadedData.form
            in
            ( Loaded
                { loadedData
                    | fileUploads = newFileUploads
                    , form = newForm
                }
            , Cmd.batch
                [ inspect (Form.encodeValues newForm)
                , if file.size <= maxSize.maxSize then
                    Http.post
                        { body = Http.jsonBody (File.encode file)
                        , expect = Http.expectJson (Uploaded key file) Decode.string
                        , url = "http://localhost:8080/public/upload"
                        }

                  else
                    Cmd.none
                ]
            )

        ( Uploaded key file (Ok fileName), Loaded loadedData ) ->
            let
                ( newFileUploads, newForm ) =
                    FileUpload.insertUploaded key
                        file
                        fileName
                        loadedData.fileUploads
                        loadedData.form
            in
            ( Loaded
                { loadedData
                    | fileUploads = newFileUploads
                    , form = newForm
                }
            , inspect (Form.encodeValues newForm)
            )

        ( Uploaded key file (Err error), Loaded loadedData ) ->
            let
                ( newFileUploads, newForm ) =
                    FileUpload.insertFailed key
                        file
                        error
                        loadedData.fileUploads
                        loadedData.form
            in
            ( Loaded
                { loadedData
                    | fileUploads = newFileUploads
                    , form = newForm
                }
            , inspect (Form.encodeValues newForm)
            )

        ( Uploaded _ _ _, _ ) ->
            pass

        ( PagesChanged (Err decodeError), Loaded loadedData ) ->
            ( Crashed
                { defaultLocale = loadedData.defaultLocale
                , locale = loadedData.locale
                , error = DecodeError decodeError
                }
            , Cmd.none
            )

        ( PagesChanged (Ok newPages), Loaded loadedData ) ->
            ( Loaded
                { loadedData
                    | pages = newPages
                }
            , Cmd.none
            )

        ( LocaleChanged (Err decodeError), Loaded loadedData ) ->
            ( Crashed
                { defaultLocale = loadedData.defaultLocale
                , locale = loadedData.locale
                , error = DecodeError decodeError
                }
            , Cmd.none
            )

        ( LocaleChanged (Ok newLocale), Loaded loadedData ) ->
            ( Loaded
                { loadedData
                    | locale = newLocale
                }
            , Cmd.none
            )

        ( Submitted (Err _), Loaded loadedData ) ->
            ( Crashed
                { defaultLocale = loadedData.defaultLocale
                , locale = loadedData.locale
                , error = CustomError "internal error"
                }
            , Cmd.none
            )

        ( Submitted (Err _), _ ) ->
            ( model, Cmd.none )

        ( Submitted (Ok (SubmissionOk _)), Loaded loadedData ) ->
            ( Finished
                { confirmationText = loadedData.confirmationText
                , locale = loadedData.locale
                , defaultLocale = loadedData.defaultLocale
                , idempotencyToken = loadedData.idempotencyToken
                }
            , Cmd.none
            )

        ( Submitted (Ok (PaymentRequired checkout)), Loaded loadedData ) ->
            ( Paying
                { defaultLocale = loadedData.defaultLocale
                , locale = loadedData.locale
                , checkout = checkout
                }
            , mountCheckout (Codec.encoder Checkout.codec checkout)
            )

        ( Submitted (Ok (SubmissionException { error })), Loaded loadedData ) ->
            ( Crashed
                { defaultLocale = loadedData.defaultLocale
                , locale = loadedData.locale
                , error = CustomError error
                }
            , Cmd.none
            )

        ( Submitted (Ok (SubmissionException _)), _ ) ->
            pass

        ( TicketsChanged (Err decodeError), Loaded loadedData ) ->
            ( Crashed
                { defaultLocale = loadedData.defaultLocale
                , locale = loadedData.locale
                , error = DecodeError decodeError
                }
            , Cmd.none
            )

        ( TicketsChanged (Ok newTickets), Loaded loadedData ) ->
            ( Loaded
                { loadedData
                    | tickets = newTickets
                }
            , Cmd.none
            )

        ( DiscountsChanged (Err decodeError), Loaded loadedData ) ->
            ( Crashed
                { defaultLocale = loadedData.defaultLocale
                , locale = loadedData.locale
                , error = DecodeError decodeError
                }
            , Cmd.none
            )

        ( DiscountsChanged (Ok newDiscounts), Loaded loadedData ) ->
            ( Loaded
                { loadedData
                    | discounts = newDiscounts
                }
            , Cmd.none
            )

        ( _, Finished _ ) ->
            pass

        ( _, Closed _ ) ->
            pass

        ( _, Paying _ ) ->
            pass


view : Model -> Html Msg
view model =
    case model of
        Crashed data ->
            viewCrashed data

        QuotaExceeded data ->
            viewQuotaExceeded data

        Loaded data ->
            viewLoaded data

        Loading ->
            viewLoading

        Finished data ->
            viewFinished data

        Paying data ->
            viewPaying data

        Closed data ->
            viewClosed data


viewCrashed : CrashedData -> Html Msg
viewCrashed data =
    Html.div [ class "error-view" ]
        [ Result.unpack
            (\error -> Html.pre [] [ Html.text error ])
            (Html.div [])
            (Markdown.view
                (Translated.extractDef data
                    ""
                    (Translated.fromLocale
                        (\locale ->
                            case locale of
                                Locale.De ->
                                    """
# Technischer Fehler

Leider ist ein unerwarteter Fehler aufgetreten.
"""

                                Locale.En ->
                                    """
# Technical error

Unfortunately, we have encountered an unexpected error.
"""

                                Locale.Es ->
                                    """
# Error técnico

Desafortunadamente, se ha producido un error inesperado."
"""

                                Locale.Fr ->
                                    """
Erreur technique

Une erreur inattendue est malheureusement survenue.
"""

                                Locale.Ru ->
                                    """
Сбой в работе оборудования

К сожалению, мы столкнулись с непредвиденной ошибкой.
"""
                        )
                    )
                )
            )
        , Html.details []
            [ Html.summary []
                [ Html.text
                    (Translated.extractDef data
                        ""
                        (Translated.fromLocale
                            (\locale ->
                                case locale of
                                    Locale.De ->
                                        "Technische Details"

                                    Locale.En ->
                                        "Technical details"

                                    Locale.Es ->
                                        "Detalles técnicos"

                                    Locale.Fr ->
                                        "Détails techniques"

                                    Locale.Ru ->
                                        "Техническая информация, подробности"
                            )
                        )
                    )
                ]
            , Html.pre []
                [ Html.text (errorToString data.error)
                ]
            ]
        , Result.unpack
            (\error -> Html.pre [] [ Html.text error ])
            (Html.div [])
            (Markdown.view
                (Translated.extractDef data
                    ""
                    (Translated.fromLocale
                        (\locale ->
                            case locale of
                                Locale.De ->
                                    """
Bei Fragen zur Registrierung stehen wir Ihnen gern unter den folgenden Kontaktdaten zur Verfügung:

Frau Sandrine Czech, Telefon: [+49(0)511 898813-17](tel:+49511898813-17) - E-Mail: [registrierungen@congresscheck.de](mailto:registrierungen@congresscheck.de)
"""

                                Locale.En ->
                                    """
Please do not hesitate to contact us in case of any further questions regarding your registration.

Mrs. Sandrine Czech, phone: [+49(0)511 898813-17](tel:+49511898813-17) - E-Mail: [registrierungen@congresscheck.de](mailto:registrierungen@congresscheck.de)
"""

                                Locale.Es ->
                                    """
Si tiene alguna pregunta sobre la inscripción, no dude en ponerse en contacto con nosotros utilizando los siguientes datos de contacto:

Sra. Sandrine Czech, Teléfono: [+49(0)511 898813-17](tel:+49511898813-17) - Correo electrónico: [registrierungen@congresscheck.de](mailto:registrierungen@congresscheck.de)
"""

                                Locale.Fr ->
                                    """
Si vous avez des questions concernant l'enregistrement, n'hésitez pas à nous contacter aux coordonnées suivantes:

Mme Sandrine Czech, tél.: [+49(0)511 898813-17](tel:+49511898813-17) - E-mail: [registrierungen@congresscheck.de](mailto:registrierungen@congresscheck.de)
"""

                                Locale.Ru ->
                                    """
Если у вас возникнут дополнительные вопросы относительно вашей регистрации, обращайтесь к нам:

Г-жа Сандрине Чех, телефон: [+49(0)511 898813-17](tel:+49511 898813-17) - Электронная почта: [registrierungen@congresscheck.de](mailto:registrierungen@congresscheck.de)
"""
                        )
                    )
                )
            )
        ]


viewQuotaExceeded : QuotaExceededData -> Html Msg
viewQuotaExceeded data =
    Html.div [ class "error-view" ]
        [ Result.unpack
            (\error -> Html.pre [] [ Html.text error ])
            (Html.div [])
            (Markdown.view
                (Translated.extractDef data
                    ""
                    (Translated.fromLocale
                        (\locale ->
                            case locale of
                                Locale.De ->
                                    """
# Kontingent erschöpft

Das Kontingent für den Code **"""
                                        ++ data.code
                                        ++ """** ist erschöpft. Haben Sie sich verschrieben oder sind Sie bereits angemeldet?
"""

                                Locale.En ->
                                    """
# Quota Exceeded

The quota for the code **"""
                                        ++ data.code
                                        ++ """** is exceeded. Did you spell the code incorrectly, or have you registered already?
"""

                                Locale.Es ->
                                    """
# Contingente agotado

Se ha superado la cuota para el código **"""
                                        ++ data.code
                                        ++ """** . ¿Ha escrito mal el código o ya se has registrado anteriormente?
"""

                                Locale.Fr ->
                                    """
Contingent dépassé

Le contingent pour le code **""" ++ data.code ++ """** est dépassé. Avez-vous fait une erreur ou êtes-vous déjà inscrit ?
"""

                                Locale.Ru ->
                                    """
Квота исчерпана

Квота для кода **"""
                                        ++ data.code
                                        ++ """** исчерпана. Вы допустили опечатку или уже зарегистрировались?
"""
                        )
                    )
                )
            )
        , Result.unpack
            (\error -> Html.pre [] [ Html.text error ])
            (Html.div [])
            (Markdown.view
                (Translated.extractDef data
                    ""
                    (Translated.fromLocale
                        (\locale ->
                            case locale of
                                Locale.De ->
                                    """
Bei Fragen zur Registrierung stehen wir Ihnen gern unter den folgenden Kontaktdaten zur Verfügung:

Frau Sandrine Czech, Telefon: [+49(0)511 898813-17](tel:+49511898813-17) - E-Mail: [registrierungen@congresscheck.de](mailto:registrierungen@congresscheck.de)
"""

                                Locale.En ->
                                    """
Please do not hesitate to contact us in case of any further questions regarding your registration.

Mrs. Sandrine Czech, phone: [+49(0)511 898813-17](tel:+49511898813-17) - E-Mail: [registrierungen@congresscheck.de](mailto:registrierungen@congresscheck.de)
"""

                                Locale.Es ->
                                    """
Si tiene alguna pregunta sobre la inscripción, no dude en ponerse en contacto con nosotros utilizando los siguientes datos de contacto:

Sra. Sandrine Czech, Teléfono: [+49(0)511 898813-17](tel:+49511898813-17) - Correo electrónico: [registrierungen@congresscheck.de](mailto:registrierungen@congresscheck.de)
"""

                                Locale.Fr ->
                                    """
Si vous avez des questions concernant l'enregistrement, n'hésitez pas à nous contacter aux coordonnées suivantes:

Mme Sandrine Czech, tél.: [+49(0)511 898813-17](tel:+49511898813-17) - E-mail: [registrierungen@congresscheck.de](mailto:registrierungen@congresscheck.de)
"""

                                Locale.Ru ->
                                    """
Если у вас возникнут дополнительные вопросы относительно вашей регистрации, обращайтесь к нам:

Г-жа Сандрине Чех, телефон: [+49(0)511 898813-17](tel:+49511 898813-17) - Электронная почта: [registrierungen@congresscheck.de](mailto:registrierungen@congresscheck.de)
"""
                        )
                    )
                )
            )
        ]


viewLoaded : LoadedData -> Html Msg
viewLoaded data =
    let
        values =
            data.form

        form =
            Page.toForm data.pages
    in
    Html.div []
        [ debugBanner data
        , Html.map FormChanged
            (Form.toHtml (formConfig data)
                [ Html.Attributes.attribute "style" (colorSchemeToStyle data.settings.colorScheme)
                ]
                values
                form
            )
        ]


formConfig : LoadedData -> Form.Context Ticket
formConfig data =
    { defaultLocale = data.defaultLocale
    , locale = data.locale
    , code = data.code
    , tickets = data.tickets
    , discounts = data.discounts
    , settings = data.settings
    , fileUploads = data.fileUploads
    }


debugBanner :
    { c
        | settings : Settings
        , defaultLocale : Locale
        , locale : Locale
    }
    -> Html msg
debugBanner config =
    if not config.settings.live then
        Html.div [ class "debug-banner" ]
            [ Html.text
                (Translated.extractDef config
                    ""
                    (Translated.fromLocale
                        (\locale ->
                            case locale of
                                Locale.De ->
                                    "Die Registrierung befindet sich in der Entwicklung. Zahlung ist NICHT live."

                                Locale.En ->
                                    "This registration is being developer. Payment is NOT live."

                                Locale.Es ->
                                    "La inscripción está en desarrollo. El pago NO está activo."

                                Locale.Fr ->
                                    "L'inscription est en cours de réalisation. Le paiement n'est PAS en direct."

                                Locale.Ru ->
                                    "Эта регистрация находится на стадии разработки. Функция оплаты НЕ в режиме «лайф»."
                        )
                    )
                )
            ]

    else
        Html.text ""


colorSchemeToStyle : ColorScheme -> String
colorSchemeToStyle colorScheme =
    String.join " "
        (List.map (\( key, value ) -> key ++ ": " ++ Color.colorToHex value ++ ";")
            [ ( "--mdc-theme-primary", colorScheme.primary )
            , ( "--mdc-theme-secondary", colorScheme.secondary )
            , ( "--mdc-theme-error", colorScheme.error )

            -- , ( "--mdc-theme-background", colorScheme.background )
            -- , ( "--mdc-theme-surface", colorScheme.surface )
            -- , ( "--mdc-theme-on-primary", colorscheme.onPrimary )
            --, ( "--mdc-theme-on-secondary", colorScheme.onSecondary )
            --, ( "--mdc-theme-on-surface", colorScheme.onSurface )
            -- , ( "--mdc-theme-text-primary-on-light", colorScheme.primaryOnLight )
            -- , ( "--mdc-theme-text-secondary-on-light", colorScheme.secondaryOnLight )
            -- , ( "--mdc-theme-text-hint-on-light", colorScheme.hintOnLight )
            -- , ( "--mdc-theme-text-disabled-on-light", colorScheme.disabledOnLight )
            -- , ( "--mdc-theme-text-icon-on-light", colorScheme.iconOnLight )
            -- , ( "--mdc-theme-text-primary-on-dark", colorScheme.primaryOnDark )
            -- , ( "--mdc-theme-text-secondary-on-dark", colorScheme.secondaryOnDark )
            -- , ( "--mdc-theme-text-hint-on-dark", colorScheme.hintOnDark )
            -- , ( "--mdc-theme-text-disabled-on-dark", colorScheme.disabledOnDark )
            -- , ( "--mdc-theme-text-icon-on-dark", colorScheme.iconOnDark )
            ]
        )


viewLoading : Html msg
viewLoading =
    Html.div
        [ class "loading"
        ]
        [ CircularProgress.indeterminate CircularProgress.config
        ]


viewFinished :
    FinishedData
    -> Html Msg
viewFinished data =
    Result.unpack
        (\error ->
            viewCrashed
                { defaultLocale = data.defaultLocale
                , locale = data.locale
                , error = MarkdownError error
                }
        )
        (Html.div [])
        (Markdown.view (Translated.extractDef data "" data.confirmationText))


viewClosed :
    ClosedData
    -> Html Msg
viewClosed data =
    Result.unpack
        (\error ->
            viewCrashed
                { defaultLocale = data.defaultLocale
                , locale = data.locale
                , error = MarkdownError error
                }
        )
        (Html.div [])
        (Markdown.view (Translated.extractDef data "" data.closedText))


viewPaying : PayingData -> Html msg
viewPaying data =
    Html.div [ class "error-view" ]
        [ Result.unpack
            (\error -> Html.pre [] [ Html.text error ])
            (Html.div [])
            (Markdown.view
                (Translated.extractDef data
                    ""
                    (Translated.fromLocale
                        (\locale ->
                            case locale of
                                Locale.De ->
                                    """
# Zahlung

Bitte schließen Sie die Zahlung bei unserem externen Zahlungsprovider ab.
"""

                                Locale.En ->
                                    """
# Checkout

Please complete the payment with our external payment provider.
"""

                                Locale.Es ->
                                    """
# Pago

Por favor, completa el pago con nuestro proveedor de pago externo.
"""

                                Locale.Fr ->
                                    """
# Règlement

Veuillez finaliser le règlement auprès de notre prestataire de paiement externe.
"""

                                Locale.Ru ->
                                    """
# Чек-аут

Пожалуйста, завершите платеж с помощью нашего провайдера платёжных услуг
"""
                        )
                    )
                )
            )
        , Checkout.checkout data.checkout
        ]
